// Carte d'une tâche (admin_tasks) — grammaire unique DA, réutilisée dans les
// 4 vues du board (DESIGN.md §8 ter). Blanc, radius 10, soulèvement au survol
// (.fbz-card). Titre Libre Franklin 500 marine (pas un nom propre → pas
// Fraunces). Chip d'échéance porteuse de couleur, pastille logement ciel,
// avatar ciel (initiales Fraunces) poussé à droite. Tâche faite = opacity .85.
//
// PRÉSENTATION SEULE : le drag (@dnd-kit), l'ouverture de la modale et le
// toggle « fait » restent inchangés. `showStatus` est un prop d'affichage
// (les vues non-Statuts montrent le statut en pastille sur la carte).

import { useDraggable } from '@dnd-kit/core'
import { GripVertical, Clock, Home, User, CircleCheck } from 'lucide-react'
import { DS } from '../../lib/ui'
import {
  SCOPE_LABELS,
  PRIORITY_COLORS,
  PRIORITY_LABELS,
  resolveLogementNom,
  resolveProprioNom,
} from './taches-logic'
import { dueChip, initialsFrom, STATUS_PILL, STATUS_DOT } from './taches-ui'

export default function TaskCard({ task, cfg, labelsById, onEdit, onToggleDone, showStatus = false }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: task.id,
    data: { status: task.status },
  })

  const done = task.status === 'done'
  const logNom = task.scope === 'logement' ? resolveLogementNom(cfg, task.logement_id) : null
  const propNom = task.scope === 'proprio' ? resolveProprioNom(cfg, task.prop_id) : null
  const chip = dueChip(task.due_at, task.due_all_day)
  const taskLabels = (task.labels || []).map(id => labelsById[id]).filter(Boolean)
  const initials = initialsFrom(logNom || propNom || SCOPE_LABELS[task.scope] || '')
  const pill = STATUS_PILL[task.status]

  return (
    <div
      ref={setNodeRef}
      className="fbz-card"
      style={{
        background: DS.card,
        borderRadius: 10,
        boxShadow: '0 1px 3px rgba(33,83,135,.06)',
        border: `0.5px solid ${DS.border}`,
        padding: 12,
        marginBottom: 10,
        opacity: isDragging ? 0.4 : done ? 0.85 : 1,
        cursor: 'pointer',
        position: 'relative',
      }}
      data-testid="task-card"
      onClick={() => onEdit(task)}
    >
      {/* Titre + avatar + poignée de drag */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <span
          title={`Priorité ${PRIORITY_LABELS[task.priority] || task.priority}`}
          style={{
            width: 8, height: 8, borderRadius: '50%',
            background: PRIORITY_COLORS[task.priority] || DS.marine,
            marginTop: 6, flexShrink: 0,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: DS.fontBody,
              fontSize: 14,
              fontWeight: 500,
              color: done ? DS.textMuted : DS.marineDark,
              textDecoration: done ? 'line-through' : 'none',
              wordBreak: 'break-word',
              lineHeight: 1.35,
            }}
          >
            {task.title}
          </div>
        </div>
        {initials && (
          <span
            aria-hidden="true"
            style={{
              width: 26, height: 26, borderRadius: 8, flexShrink: 0,
              background: DS.ciel, color: DS.marineDeep,
              fontFamily: DS.fontDisplay, fontWeight: 600, fontSize: 11,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {initials}
          </span>
        )}
        {/* Poignée de drag — n'ouvre PAS la modale (stopPropagation) */}
        <span
          {...listeners}
          {...attributes}
          onClick={e => e.stopPropagation()}
          title="Glisser pour déplacer"
          style={{
            cursor: 'grab', color: DS.textMuted, display: 'flex', alignItems: 'center',
            padding: '0 1px', touchAction: 'none', flexShrink: 0,
          }}
          data-testid="task-drag-handle"
        >
          <GripVertical size={16} strokeWidth={2} />
        </span>
      </div>

      {/* Méta : échéance · logement/proprio · statut (vues non-Statuts) */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 8 }}>
        {chip && (
          <span
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 7,
              background: chip.bg, color: chip.fg,
            }}
          >
            <Clock size={12} strokeWidth={2} /> {chip.label}
          </span>
        )}
        {logNom && (
          <span style={pastille(DS.cielSoft, DS.marine)}>
            <Home size={12} strokeWidth={2} /> {logNom}
          </span>
        )}
        {propNom && (
          <span style={pastille(DS.corailSoft, DS.corailText)}>
            <User size={12} strokeWidth={2} /> {propNom}
          </span>
        )}
        {!logNom && !propNom && (
          <span style={pastille('#eef1f4', DS.textSecondary)}>
            {SCOPE_LABELS[task.scope] || task.scope}
          </span>
        )}
        {showStatus && pill && (
          <span style={{ ...pastille(pill.bg, pill.fg), gap: 5 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: STATUS_DOT[task.status] }} />
            {SCOPE_STATUS_LABEL[task.status]}
          </span>
        )}
      </div>

      {/* Étiquettes thématiques (couleur = fonction de l'étiquette) */}
      {taskLabels.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
          {taskLabels.map(l => (
            <span
              key={l.id}
              style={{
                fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 8,
                background: `${l.color}1f`, color: l.color, border: `0.5px solid ${l.color}55`,
              }}
            >
              {l.emoji ? `${l.emoji} ` : ''}{l.name}
            </span>
          ))}
        </div>
      )}

      {/* Aperçu note (tronqué) */}
      {task.note && (
        <div
          style={{
            marginTop: 8, fontSize: 12, color: DS.textSecondary, lineHeight: 1.4,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}
        >
          {task.note}
        </div>
      )}

      {/* Coche « fait » */}
      <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          className="fbz-press"
          onClick={e => { e.stopPropagation(); onToggleDone(task) }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: 12, fontWeight: 600,
            border: `0.5px solid ${done ? DS.success : DS.borderHover}`,
            background: done ? DS.successBg : DS.card,
            color: done ? DS.successText : DS.marine,
            borderRadius: 8, padding: '4px 10px', cursor: 'pointer',
          }}
          data-testid="task-toggle-done"
        >
          {done ? <CircleCheck size={14} strokeWidth={2} /> : null}
          {done ? 'Fait' : 'Marquer fait'}
        </button>
      </div>
    </div>
  )
}

const SCOPE_STATUS_LABEL = { todo: 'À faire', doing: 'En cours', done: 'Fait' }

function pastille(bg, fg) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 7,
    background: bg, color: fg, maxWidth: '100%',
  }
}
